import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import { useAuth } from 'shared/context/AuthContext';

import PersonalInformation from './components/PersonalInformation';
import SecuritySettings from './components/SecuritySettings';
import Preferences from './components/Preferences';
import ConnectedDevices from './components/ConnectedDevices';

const UserProfileSettings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('personal');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const tabs = [
    {
      id: 'personal',
      label: 'Личная информация',
      icon: 'User',
      component: PersonalInformation
    },
    {
      id: 'security',
      label: 'Безопасность',
      icon: 'Shield',
      component: SecuritySettings
    },
    {
      id: 'preferences',
      label: 'Настройки',
      icon: 'Settings',
      component: Preferences
    },
    {
      id: 'devices',
      label: 'Устройства',
      icon: 'Smartphone',
      component: ConnectedDevices
    }
  ];

  // Build user display data from auth context
  const getInitials = () => {
    if (!user) return 'U';
    const name = user.name || '';
    const surname = user.surname || '';
    if (name && surname) {
      return `${name[0]}${surname[0]}`.toUpperCase();
    }
    if (name) return name.slice(0, 2).toUpperCase();
    if (user.username) return user.username.slice(0, 2).toUpperCase();
    return 'U';
  };

  const getFullName = () => {
    if (!user) return 'Пользователь';
    const name = user.name || '';
    const surname = user.surname || '';
    if (name && surname) return `${name} ${surname}`;
    if (name) return name;
    return user.username || 'Пользователь';
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Администратор';
      case 'user': return 'Пользователь';
      default: return role || 'Пользователь';
    }
  };

  const currentUser = {
    name: getFullName(),
    email: user?.email || '',
    role: getRoleLabel(user?.role),
    avatar: user?.avatarUrl || null,
    initials: getInitials(),
    isActive: user?.isActive ?? true
  };

  const handleTabChange = (tabId) => {
    if (hasUnsavedChanges) {
      if (window.confirm('У вас есть несохраненные изменения. Вы уверены, что хотите покинуть эту вкладку?')) {
        setActiveTab(tabId);
        setHasUnsavedChanges(false);
      }
    } else {
      setActiveTab(tabId);
    }
  };

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="User" size={24} className="text-primary" />
            <h1 className="text-2xl font-semibold text-text-primary">
              Настройки профиля
            </h1>
          </div>
          <p className="text-text-secondary">
            Управляйте своей личной информацией, настройками безопасности и предпочтениями
          </p>
        </div>

        {/* Profile Summary Card */}
        <div className="bg-surface rounded-lg border border-secondary-200 p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
              <span className="text-xl font-semibold text-white">
                {currentUser.initials}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-text-primary">
                {currentUser.name}
              </h2>
              <p className="text-text-secondary">{currentUser.email}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="inline-flex items-center gap-1 text-sm text-accent">
                  <Icon name="Shield" size={14} />
                  {currentUser.role}
                </span>
                <span className={`inline-flex items-center gap-1 text-sm ${currentUser.isActive ? 'text-success' : 'text-error'}`}>
                  <Icon name={currentUser.isActive ? 'CheckCircle' : 'XCircle'} size={14} />
                  {currentUser.isActive ? 'Активен' : 'Деактивирован'}
                </span>
              </div>
            </div>
            {hasUnsavedChanges && (
              <div className="flex items-center gap-2 text-warning">
                <Icon name="AlertCircle" size={16} />
                <span className="text-sm font-medium">Несохраненные изменения</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Desktop Tabs - Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-surface rounded-lg border border-secondary-200 p-4 shadow-sm">
              <nav className="space-y-2">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-md text-left
                      transition-all duration-150 ease-out hover-lift
                      ${activeTab === tab.id
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-primary hover:bg-secondary-50 hover:text-primary-600'
                      }
                    `}
                  >
                    <Icon 
                      name={tab.icon} 
                      size={18} 
                      className={activeTab === tab.id ? 'text-white' : 'text-secondary-500'}
                    />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Mobile Tabs - Horizontal */}
          <div className="lg:hidden col-span-1 mb-6">
            <div className="bg-surface rounded-lg border border-secondary-200 p-2 shadow-sm">
              <div className="flex overflow-x-auto gap-2">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-md whitespace-nowrap
                      transition-all duration-150 ease-out min-w-touch
                      ${activeTab === tab.id
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-primary hover:bg-secondary-50'
                      }
                    `}
                  >
                    <Icon 
                      name={tab.icon} 
                      size={16} 
                      className={activeTab === tab.id ? 'text-white' : 'text-secondary-500'}
                    />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <div className="bg-surface rounded-lg border border-secondary-200 shadow-sm">
              {ActiveComponent && (
                <ActiveComponent 
                  onUnsavedChanges={setHasUnsavedChanges}
                  hasUnsavedChanges={hasUnsavedChanges}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileSettings;